import React from 'react';
import { ScrollView, StyleSheet, View, type TextStyle } from 'react-native';

import { Text, useTheme } from '@/components/ui';
import type { ExteriorApplicationDetail, ExteriorImprovementsReport } from '@/services/data-service';
import { formatBusinessDate } from '@/utils/date-utils';
import { momentToBusinessMs } from '@/utils/exterior-improvements-report';

const countsLine = (counts: Record<string, number>): string =>
	Object.entries(counts).map(([k, n]) => `${k} ${n}`).join(' · ');

type ColumnKey =
	| 'number' | 'lot' | 'address' | 'owner' | 'type' | 'status'
	| 'submitted' | 'decided' | 'decidedBy' | 'board' | 'description';

interface Column {
	key: ColumnKey;
	title: string;
	/** Screen width; `null` = the flexible column that takes the rest. */
	width: number | null;
	/** Compact width for letter-portrait paper, which also carries the board packets. */
	printWidth: number | null;
	cell: (item: ExteriorApplicationDetail) => string;
}

const COLUMNS: Column[] = [
	{ key: 'number', title: 'App #', width: 56, printWidth: 30, cell: (i) => (i.applicationNumber != null ? `#${i.applicationNumber}` : '—') },
	{ key: 'lot', title: 'Lot', width: 70, printWidth: 40, cell: (i) => i.accountNumber ?? '—' },
	{ key: 'address', title: 'Address', width: 180, printWidth: 96, cell: (i) => i.propertyAddress ?? i.accountAddress ?? '—' },
	{ key: 'owner', title: 'Owner', width: 150, printWidth: 84, cell: (i) => i.accountOwnerName ?? i.submitterName ?? '—' },
	{ key: 'type', title: 'Type', width: 120, printWidth: 64, cell: (i) => i.requestTypeName ?? '—' },
	{ key: 'status', title: 'Status', width: 120, printWidth: 60, cell: (i) => i.statusLabel ?? '—' },
	// Real moments, rendered as their ET business day.
	{ key: 'submitted', title: 'Submitted', width: 90, printWidth: 56, cell: (i) => formatBusinessDate(momentToBusinessMs(i.createdAt)) },
	{ key: 'decided', title: 'Decided', width: 90, printWidth: 56, cell: (i) => formatBusinessDate(momentToBusinessMs(i.decidedAt)) },
	{ key: 'decidedBy', title: 'Decided by', width: 120, printWidth: 60, cell: (i) => i.decidedByName ?? '—' },
	{ key: 'board', title: 'Board meeting', width: 100, printWidth: 56, cell: (i) => formatBusinessDate(i.boardMeetingDate) },
	{ key: 'description', title: 'Description', width: null, printWidth: null, cell: (i) => i.description ?? '' },
];

/** Cells whose testIDs the specs and browser walks already address. */
const CELL_TEST_ID: Partial<Record<ColumnKey, string>> = { decided: 'decided', submitted: 'submitted' };

const columnsFor = (print: boolean): Column[] =>
	print ? COLUMNS.filter((column) => column.key !== 'description') : COLUMNS;

const columnStyle =(column: Column, print: boolean): TextStyle => {
	const width = print ? column.printWidth : column.width;
	return width == null ? { flex: 1, minWidth: print ? 0 : 220 } : { width };
};

const HeaderRow: React.FC<{ print: boolean }> = ({ print }) => {
	const theme = useTheme();
	return (
		<View
			testID="report-table-header"
			style={[styles.tableRow, styles.headerRow, { borderBottomColor: theme.colors.outline }]}
		>
			{columnsFor(print).map((column) => (
				<Text
					key={column.key}
					style={[styles.cell, print && styles.printCell, styles.headerCell, columnStyle(column, print)]}
				>
					{column.title}
				</Text>
			))}
		</View>
	);
};

const ReportRow: React.FC<{ item: ExteriorApplicationDetail; print: boolean }> = ({ item, print }) => {
	const theme = useTheme();
	const cells = columnsFor(print).map((column) => {
		const suffix = CELL_TEST_ID[column.key];
		return (
			<Text
				key={column.key}
				testID={suffix ? `report-row-${item.id}-${suffix}` : undefined}
				numberOfLines={column.key === 'description' && !print ? 2 : undefined}
				style={[styles.cell, print && styles.printCell, columnStyle(column, print)]}
			>
				{column.cell(item)}
			</Text>
		);
	});
	if (!print) {
		return (
			<View
				testID={`report-row-${item.id}`}
				style={[styles.tableRow, { borderBottomColor: theme.colors.outlineVariant }]}
			>
				{cells}
			</View>
		);
	}
	// Paper: the fixed-width columns leave the description a sliver, so it gets its own full-width line.
	return (
		<View
			testID={`report-row-${item.id}`}
			style={[styles.printRow, { borderBottomColor: theme.colors.outlineVariant }]}
		>
			<View style={styles.printCells}>{cells}</View>
			{item.description ? (
				<Text testID={`report-row-${item.id}-description`} style={[styles.cell, styles.printCell]}>
					{item.description}
				</Text>
			) : null}
		</View>
	);
};

const Table: React.FC<{ items: ExteriorApplicationDetail[]; print: boolean }> = ({ items, print }) => {
	const body = (
		<View style={print ? undefined : styles.screenTable}>
			<HeaderRow print={print} />
			{items.map((item) => <ReportRow key={item.id} item={item} print={print} />)}
		</View>
	);
	// Paper has a fixed width; on screen the columns keep their widths and the table scrolls.
	return print ? body : <ScrollView horizontal>{body}</ScrollView>;
};

/**
 * The Exterior Improvements report body (#644), shared by the screen and the print route:
 * one section per association (server-sorted), its status/type counts, then a table with one
 * row per request under a header row.
 */
export const ReportSummary: React.FC<{ report: ExteriorImprovementsReport; print?: boolean }> = ({
	report,
	print = false,
}) => {
	const theme = useTheme();
	if (report.associations.length === 0) {
		return (
			<Text style={[styles.empty, { color: theme.colors.onSurfaceVariant }]}>
				No exterior improvement requests in this range.
			</Text>
		);
	}
	return (
		<View>
			{report.associations.map((association) => (
				<View key={association.id} testID="report-association-section" style={styles.section}>
					<Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>{association.name}</Text>
					<Text style={[styles.counts, { color: theme.colors.onSurfaceVariant }]}>
						{`${association.counts.total} total · ${countsLine(association.counts.byStatus)}`}
					</Text>
					<Text style={[styles.counts, { color: theme.colors.onSurfaceVariant }]}>
						{countsLine(association.counts.byType)}
					</Text>
					<Table items={association.items} print={print} />
				</View>
			))}
		</View>
	);
};

const styles = StyleSheet.create({
	empty: { paddingVertical: 24, textAlign: 'center' },
	section: { marginBottom: 20 },
	sectionTitle: { fontSize: 16, fontWeight: '700' },
	counts: { fontSize: 12, marginTop: 2 },
	screenTable: { minWidth: '100%' },
	tableRow: { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 6, borderBottomWidth: StyleSheet.hairlineWidth },
	printRow: { paddingVertical: 6, borderBottomWidth: StyleSheet.hairlineWidth },
	printCells: { flexDirection: 'row', alignItems: 'flex-start' },
	headerRow: { borderBottomWidth: 1, marginTop: 8 },
	cell: { fontSize: 13, paddingRight: 8 },
	printCell: { fontSize: 9, paddingRight: 4 },
	headerCell: { fontWeight: '700' },
});
